import { useEffect, useMemo, useRef, useState, KeyboardEvent } from "react";
import { Play, ExternalLink, Clock, Swords, Youtube, Server } from "lucide-react";
import { Link } from "react-router-dom";
import YouTubeEmbed from "@/components/YouTubeEmbed";
import { supabase } from "@/integrations/supabase/client";

export interface HeroDebateItem {
  title: string;
  subtitle: string;
  year: string;
  videoId: string;
}

interface Props {
  debates: HeroDebateItem[];
  className?: string;
}

/** Per-video playback source badge state. */
type SourceState = "youtube" | "mux" | "unknown";

/**
 * Hybrid hero video showcase: one large featured player on top, with a
 * horizontally-scrollable thumbnail strip below. Click any thumbnail to
 * swap it into the featured player. Supports:
 *   • keyboard navigation (←/→/Home/End) with visible focus outline
 *   • auto-scroll-to-center for the active thumbnail
 *   • per-video badge showing whether a Mux mirror is ready
 *   • Arena deep-link from the featured player
 *   • visible Retry on the embed-blocked fallback (key bumps remount)
 */
const HeroDebateShowcase = ({ debates, className }: Props) => {
  const [activeIdx, setActiveIdx] = useState(0);
  const [retryNonce, setRetryNonce] = useState(0);
  const [sources, setSources] = useState<Record<string, SourceState>>({});
  const stripRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const active = debates[activeIdx];

  // Batch-resolve which videos have a ready Mux mirror so we can label each
  // thumbnail with its current playback source.
  useEffect(() => {
    if (!debates.length) return;
    let cancelled = false;
    const ids = debates.map((d) => d.videoId);
    supabase
      .from("youtube_mux_mirrors")
      .select("youtube_video_id, mirror_status, mux_playback_id")
      .in("youtube_video_id", ids)
      .then(({ data }) => {
        if (cancelled) return;
        const next: Record<string, SourceState> = {};
        for (const id of ids) next[id] = "youtube";
        for (const row of data || []) {
          const r = row as { youtube_video_id: string; mirror_status: string | null; mux_playback_id: string | null };
          if (r.mirror_status === "ready" && r.mux_playback_id) {
            next[r.youtube_video_id] = "mux";
          }
        }
        setSources(next);
      }, () => {});
    return () => { cancelled = true; };
  }, [debates]);

  // Auto-scroll the active thumbnail into the center of the strip.
  useEffect(() => {
    const node = itemRefs.current[activeIdx];
    if (!node) return;
    // Prefer the strip's horizontal centering on mobile; on desktop the strip
    // becomes a grid, so scrollIntoView with block:"nearest" stays a no-op.
    node.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [activeIdx]);

  const arenaHref = useMemo(
    () => (active ? `/arena?debate=${encodeURIComponent(active.videoId)}` : "/arena"),
    [active],
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!debates.length) return;
    if (e.key === "ArrowRight") {
      e.preventDefault();
      const next = (activeIdx + 1) % debates.length;
      setActiveIdx(next);
      itemRefs.current[next]?.focus();
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      const next = (activeIdx - 1 + debates.length) % debates.length;
      setActiveIdx(next);
      itemRefs.current[next]?.focus();
    } else if (e.key === "Home") {
      e.preventDefault();
      setActiveIdx(0);
      itemRefs.current[0]?.focus();
    } else if (e.key === "End") {
      e.preventDefault();
      const last = debates.length - 1;
      setActiveIdx(last);
      itemRefs.current[last]?.focus();
    }
  };

  if (!active) return null;

  return (
    <div
      className={`mx-auto w-full max-w-[1100px] ${className || ""}`}
      data-testid="hero-debate-showcase"
    >
      {/* Featured player */}
      <div className="relative mx-auto w-full overflow-hidden rounded-2xl border border-sovereign-gold/25 bg-card shadow-[0_20px_60px_-20px_hsl(var(--sovereign-gold)/0.35)]">
        <div className="relative aspect-video w-full">
          <YouTubeEmbed
            key={`${active.videoId}-${retryNonce}`}
            videoId={active.videoId}
            title={`${active.title} — ${active.year}`}
            className="absolute inset-0 h-full w-full"
            onRetry={() => setRetryNonce((n) => n + 1)}
          />
        </div>
        {/* Caption */}
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border/60 bg-background/70 px-4 py-3 backdrop-blur">
          <div className="min-w-0 text-left">
            <p className="truncate text-[10px] font-mono uppercase tracking-widest text-sovereign-gold">
              {active.subtitle}
            </p>
            <h3 className="truncate text-sm font-bold text-foreground md:text-base">
              {active.title}
              <span className="ml-2 text-muted-foreground">· {active.year}</span>
            </h3>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Link
              to={arenaHref}
              data-testid="hero-arena-deeplink"
              className="inline-flex items-center gap-1.5 rounded-full border border-sovereign-gold/40 bg-sovereign-gold/15 px-3 py-1 text-[11px] font-mono font-bold text-sovereign-gold transition-colors hover:bg-sovereign-gold/25"
            >
              <Swords className="h-3 w-3" /> Open in Arena
            </Link>
            <a
              href={`https://www.youtube.com/watch?v=${active.videoId}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Open ${active.title} on YouTube`}
              className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-background/60 px-3 py-1 text-[11px] font-mono text-muted-foreground transition-colors hover:border-sovereign-gold/40 hover:text-sovereign-gold"
            >
              Open on YouTube <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      </div>

      {/* Thumbnail strip — all 9 visible, swap on click */}
      <div
        ref={stripRef}
        className="mt-4 flex gap-3 overflow-x-auto pb-2 scrollbar-thin md:grid md:grid-cols-4 md:gap-3 md:overflow-visible lg:grid-cols-9"
        role="listbox"
        aria-label="Choose a debate to play"
        aria-activedescendant={`hero-debate-thumb-${activeIdx}`}
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        {debates.map((d, i) => {
          const isActive = i === activeIdx;
          const source = sources[d.videoId] ?? "unknown";
          return (
            <button
              key={d.videoId}
              id={`hero-debate-thumb-${i}`}
              ref={(el) => { itemRefs.current[i] = el; }}
              type="button"
              role="option"
              aria-selected={isActive}
              aria-label={`Play ${d.title}, ${d.subtitle} (${d.year})`}
              data-testid={`hero-debate-thumb-${i}`}
              data-active={isActive ? "true" : "false"}
              data-source={source}
              onClick={() => setActiveIdx(i)}
              tabIndex={isActive ? 0 : -1}
              className={`group relative shrink-0 overflow-hidden rounded-lg border transition-all md:shrink focus:outline-none focus-visible:ring-2 focus-visible:ring-sovereign-gold focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
                isActive
                  ? "border-sovereign-gold ring-2 ring-sovereign-gold/40"
                  : "border-border/60 hover:border-sovereign-gold/40"
              }`}
              style={{ width: "140px" }}
            >
              <div className="relative aspect-video w-full overflow-hidden bg-secondary md:w-auto">
                <img
                  src={`https://img.youtube.com/vi/${d.videoId}/mqdefault.jpg`}
                  alt={`${d.title} ${d.year}`}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/85 via-background/10 to-transparent" />
                {!isActive && (
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-sovereign-gold/90 shadow">
                      <Play className="ml-0.5 h-4 w-4 text-background" />
                    </div>
                  </div>
                )}
                {isActive && (
                  <div className="absolute inset-0 flex items-center justify-center bg-sovereign-gold/15">
                    <span className="rounded-full bg-sovereign-gold px-2 py-0.5 text-[9px] font-mono uppercase tracking-widest text-background">
                      Now playing
                    </span>
                  </div>
                )}
                <span className="absolute right-1 top-1 inline-flex items-center gap-1 rounded bg-background/70 px-1.5 py-0.5 text-[9px] font-mono text-foreground/90 backdrop-blur">
                  <Clock className="h-2.5 w-2.5" /> {d.year}
                </span>
                {/* Source indicator: Mux mirror ready vs YouTube */}
                <span
                  data-testid={`hero-debate-source-${i}`}
                  data-source={source}
                  title={
                    source === "mux"
                      ? "Playing from Mux mirror (resilient)"
                      : source === "youtube"
                      ? "Playing from YouTube"
                      : "Source unknown"
                  }
                  className={`absolute left-1 top-1 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[9px] font-mono uppercase backdrop-blur ${
                    source === "mux"
                      ? "bg-emerald-500/20 text-emerald-300 border border-emerald-400/40"
                      : "bg-background/70 text-muted-foreground border border-border/50"
                  }`}
                >
                  {source === "mux" ? (
                    <><Server className="h-2.5 w-2.5" /> Mux</>
                  ) : (
                    <><Youtube className="h-2.5 w-2.5" /> YT</>
                  )}
                </span>
              </div>
              <div className="p-1.5 text-left md:w-auto" style={{ width: "140px" }}>
                <p className="truncate text-[10px] font-bold leading-tight text-foreground">
                  {d.title}
                </p>
                <p className="truncate text-[9px] font-mono text-muted-foreground">
                  {d.subtitle}
                </p>
              </div>
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-center text-[10px] font-mono text-muted-foreground">
        Tip: use ← → keys to switch debates · Home/End to jump
      </p>
    </div>
  );
};

export default HeroDebateShowcase;